import dayjs, { Dayjs } from "dayjs";
import { IPayPeriodTemplate } from "@/business/business";
import { formatDateRange } from "@/core/utils/date";
import {
  PAYROLL_PERIOD_STATUS_DB,
  PAYROLL_RUN_STATUS,
} from "@/payroll/constants";
import {
  IPayPeriod,
  EnrichedPayPeriod,
  PayrollRunStatus,
} from "@/payroll/payroll.d";
import {
  calculatePeriodBoundaries,
  getNextPeriodStart,
} from "./pay-period-boundaries";

export const enrichPayPeriod = (period: IPayPeriod): EnrichedPayPeriod => {
  const start = dayjs(period.start_date);
  const end = dayjs(period.end_date);

  return {
    ...period,
    key: `${period.start_date}_${period.end_date}`,
    start,
    end,
    displayRange: formatDateRange(period.start_date, period.end_date),
    periodStatus: getPeriodStatus(start, end),
    existsInDb: true,
  };
};

export const isPayPeriodLocked = (
  period: { status?: IPayPeriod["status"] } | null | undefined,
): boolean => period?.status === PAYROLL_PERIOD_STATUS_DB.marked_as_paid;

export const calculateCurrentPeriod = (
  template: IPayPeriodTemplate,
): EnrichedPayPeriod => {
  const now = dayjs();
  const { start, end } = calculatePeriodBoundaries(now, template);
  return createCalculatedPayPeriod(start, end);
};

export const createCalculatedPayPeriod = (
  start: Dayjs,
  end: Dayjs,
): EnrichedPayPeriod => {
  const startDate = start.format("YYYY-MM-DD");
  const endDate = end.format("YYYY-MM-DD");

  return {
    id: `${startDate}_${endDate}`,
    key: `${startDate}_${endDate}`,
    start_date: startDate,
    end_date: endDate,
    start,
    end,
    displayRange: formatDateRange(startDate, endDate),
    periodStatus: getPeriodStatus(start, end),
    status: PAYROLL_PERIOD_STATUS_DB.pending,
    existsInDb: false,
    runs_count: 0,
    latest_run_at: null,
    latest_run_status: null,
    icm_runs_count: 0,
    latest_icm_run_id: null,
    latest_icm_run_status: null,
    latest_icm_run_at: null,
    latest_completed_icm_run_id: null,
    latest_completed_icm_run_plan_id: null,
    latest_completed_icm_run_plan_name: null,
    latest_completed_ai_script_run_id: null,
    latest_ai_script_run_status: null,
    latest_ai_script_run_at: null,
    latest_completed_ai_script_run_generated_workbook_url: null,
    latest_completed_ai_script_run_generated_workbook_metadata: null,
    roster_version_number: null,
    current_roster_version_number: null,
    ai_script_version_number: null,
    current_ai_script_version_number: null,
    latest_completed_ai_script_run_version_id: null,
    latest_completed_ai_script_run_roster_id: null,
    created_at: "",
    updated_at: "",
  };
};

export const mergePayPeriodsWithCalculatedGaps = (
  payPeriods: EnrichedPayPeriod[],
  template: IPayPeriodTemplate,
): EnrichedPayPeriod[] => {
  const periodsByStartDate = new Map(
    payPeriods.map((period) => [period.start_date, period]),
  );
  const currentPeriod = calculateCurrentPeriod(template);

  if (!periodsByStartDate.has(currentPeriod.start_date)) {
    periodsByStartDate.set(currentPeriod.start_date, currentPeriod);
  }

  const sortedKnownPeriods = Array.from(periodsByStartDate.values()).sort(
    (a, b) => a.start.diff(b.start),
  );

  if (sortedKnownPeriods.length === 0) {
    return [];
  }

  const firstPeriod = sortedKnownPeriods[0];
  const lastPeriod = sortedKnownPeriods[sortedKnownPeriods.length - 1];
  const allPeriods: EnrichedPayPeriod[] = [];

  let periodStart = firstPeriod.start;

  while (!periodStart.isAfter(lastPeriod.start, "day")) {
    const { start, end } = calculatePeriodBoundaries(periodStart, template);
    const startDate = start.format("YYYY-MM-DD");

    allPeriods.push(
      periodsByStartDate.get(startDate) ??
        createCalculatedPayPeriod(start, end),
    );
    periodStart = getNextPeriodStart(start, template);
  }

  return allPeriods.sort((a, b) => b.start.diff(a.start));
};

export const getPeriodStatus = (
  start: Dayjs,
  end: Dayjs,
): "past" | "current" | "future" => {
  const now = dayjs();
  if (now.isAfter(end, "day")) return "past";
  if (now.isBefore(start, "day")) return "future";
  return "current";
};

export const canTriggerNewRun = (
  period: EnrichedPayPeriod,
  latestRunStatus: PayrollRunStatus | null,
): boolean =>
  period.existsInDb &&
  !isPayPeriodLocked(period) &&
  latestRunStatus !== PAYROLL_RUN_STATUS.deferred &&
  latestRunStatus !== PAYROLL_RUN_STATUS.pending &&
  latestRunStatus !== PAYROLL_RUN_STATUS.processing;
